import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useRouter } from 'next/router'
import type { AppDispatch } from '@/src/store'
import { resolveFeatures } from '@/src/store/apps/featureExperience'
import { FEATURE_KEYS, TRADING_HOURS_RESTAURANT_SCOPED_RESOLVE_KEYS } from '@/src/constants/featureExperience'
import { useAuth } from '@/src/hooks/useAuth'
import { useFeatureVersion } from '@/src/hooks/useFeatureVersion'
import { ROUTES } from '@/src/utils/routes'

/**
 * Resolves **`trading_hours`** once per restaurant visit with `x-restaurant-id` (not included in global
 * {@link DEFAULT_FEATURE_RESOLVE_KEYS} — avoids duplicate `/feature-experiences/resolve` for the same key).
 */
export function useTradingHoursRestaurantScopedResolve(restaurantId: string | undefined): void {
  const dispatch = useDispatch<AppDispatch>()
  const { user } = useAuth()

  useEffect(() => {
    if (!user || !restaurantId) return
    void dispatch(
      resolveFeatures({
        keys: [...TRADING_HOURS_RESTAURANT_SCOPED_RESOLVE_KEYS],
        restaurantId
      })
    )
  }, [dispatch, user, restaurantId])
}

/**
 * Keeps URL (`/trading-hours` vs `/trading-hours/v3`) aligned with `resolvedVersion`.
 *
 * Must **not** depend on {@link useFeatureVersion}'s `isInRolloutAudience`: when `phase` is **forced**,
 * audience is false for chrome — but users still must be routed to v3 when `resolvedVersion === 'next'`.
 */
export function useTradingHoursExperienceRouteSync(restaurantId: string | undefined): void {
  const router = useRouter()
  const { version, isLoading } = useFeatureVersion(FEATURE_KEYS.TRADING_HOURS)
  const asPath = router.asPath

  useEffect(() => {
    if (!router.isReady || !restaurantId || isLoading) return

    const path = asPath.split('?')[0]
    const isV3 = /\/menu-services\/trading-hours\/v3(\/|$)/.test(path)
    const wantsV3 = version === 'next'

    if (wantsV3 === isV3) return

    if (wantsV3) {
      void router.replace(ROUTES.RESTAURANT_TRADING_HOURS_V3(restaurantId))
    } else {
      void router.replace(ROUTES.RESTAURANT_TRADING_HOURS(restaurantId))
    }
  }, [router.isReady, asPath, restaurantId, version, isLoading, router])
}
